import { useEffect, useState, useRef, useMemo } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { LogOut, Users, Mail, Inbox, Trash2, Check, Plus, Edit2, Globe, FolderGit2, X, BookOpen, FileText, Send, RefreshCw, Briefcase, Target, Building2, MapPin, ChevronLeft, ChevronRight } from "lucide-react";
import DOMPurify from "dompurify";

import ReactQuill, { Quill } from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import BlotFormatter from "@enzedonline/quill-blot-formatter2";

// Register BlotFormatter for image resize + alignment handles
Quill.register("modules/blotFormatter", BlotFormatter);




import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";

const StatCard = ({ icon: Icon, label, value }) => (
  <div className="glass rounded-2xl p-6">
    <div className="flex items-center justify-between">
      <span className="text-neutral-400 text-sm">{label}</span>
      <Icon className="w-5 h-5 text-[#FF5A1F]" />
    </div>
    <div className="font-display text-4xl font-bold mt-3">{value}</div>
  </div>
);

const fixImgUrl = (url) => (typeof url === "string" ? url.replace(/^http:\/\/localhost:8000/, "") : (url || ""));

export default function AdminDashboard() {
  const { admin, checking, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("leads");
  const [leads, setLeads] = useState([]);
  const [applications, setApplications] = useState([]);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [sendEmailModal, setSendEmailModal] = useState(null);
  const [sendingBulk, setSendingBulk] = useState(false);
  const [jobs, setJobs] = useState([]);


  const [subs, setSubs] = useState([]);
  const [blogEditorTab, setBlogEditorTab] = useState("visual");
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);


  const quillRef = useRef(null);

  const handleQuillImageUpload = () => {
    const input = document.createElement("input");
    input.setAttribute("type", "file");
    input.setAttribute("accept", "image/*");
    input.click();

    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      toast.loading("Uploading image into article...");
      try {
        const fd = new FormData();
        fd.append("file", file);
        const r = await api.post("/upload/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
        toast.dismiss();
        toast.success("Image added to document!");
        const imgUrl = fixImgUrl(r.data.url);

        const editor = quillRef.current?.getEditor();
        if (editor) {
          const range = editor.getSelection(true);
          editor.insertEmbed(range.index, "image", imgUrl);
          editor.setSelection(range.index + 1);
        }
      } catch {
        toast.dismiss();
        toast.error("Image upload failed");
      }
    };
  };

  const quillModules = useMemo(() => ({
    toolbar: {
      container: [
        [{ font: [] }, { size: ["small", false, "large", "huge"] }],
        [{ header: [1, 2, 3, 4, 5, 6, false] }],
        ["bold", "italic", "underline", "strike"],
        [{ color: [] }, { background: [] }],
        [{ script: "sub" }, { script: "super" }],
        ["blockquote", "code-block"],
        [{ list: "ordered" }, { list: "bullet" }, { indent: "-1" }, { indent: "+1" }],
        [{ direction: "rtl" }, { align: ["", "center", "right", "justify"] }],
        ["link", "image", "video"],
        ["clean"]
      ],
      handlers: {
        image: handleQuillImageUpload
      }
    },
    blotFormatter: {}
  }), []);

  const quillFormats = [
    "font", "size", "header",
    "bold", "italic", "underline", "strike",
    "color", "background",
    "script",
    "blockquote", "code-block",
    "list", "bullet", "indent",
    "direction", "align",
    "link", "image", "video",
    "width", "height", "style"
  ];



  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [stats, setStats] = useState({ leads: 0, new_leads: 0, subscribers: 0 });

  const [settings, setSettings] = useState({
    corporate_address: "Neuravolt Technology, E Block, 511, Amity Innovation Center, Gwalior, MP - 474020, India",
    registered_address: "House No 2, Sai Dham Colony, DD Nagar, Gwalior - 474005",

    email: "hello@neuravolt.in",
    phone: "+91 70000 00000",
    legal_entity: "VOLTNOVATE SOLUTIONS LLP"
  });

  // Modal / Form state for Projects
  const [projectForm, setProjectForm] = useState(null); // { id?, title, domain, cat, desc, tech, img }
  const [imgUploading, setImgUploading] = useState(false);
  // Modal / Form state for Clients
  const [clientForm, setClientForm] = useState(null); // { id?, name, domain }
  // Modal / Form state for Jobs
  const [jobForm, setJobForm] = useState(null); // { id?, title, type, location, team, category }
  // Modal / Form state for Team Members
  const [teamMembers, setTeamMembers] = useState([]);
  const [teamForm, setTeamForm] = useState(null); // { id?, name, role, initials, img, bio }
  // Blog state
  const [blogs, setBlogs] = useState([]);
  const [blogForm, setBlogForm] = useState(null); // null = closed, {} = new, {...} = edit
  const [blogImgUploading, setBlogImgUploading] = useState(false);

  const load = async () => {
    try {
      const [l, apps, j, s, st, pr, cl, sett, tm, bl, tmpl] = await Promise.all([
        api.get("/contacts"),
        api.get("/applications"),
        api.get("/jobs"),
        api.get("/newsletter"),
        api.get("/stats"),
        api.get("/projects"),
        api.get("/clients"),
        api.get("/settings"),
        api.get("/team"),
        api.get("/blogs"),
        api.get("/email-templates").catch(() => ({ data: [] })),
      ]);
      setLeads(l.data.filter((x) => !x.service?.startsWith("Career Application")));
      setApplications(apps.data);
      setJobs(j.data);
      setSubs(s.data);
      setStats(st.data);
      setProjects(pr.data);
      setClients(cl.data);
      if (sett.data) setSettings(sett.data);
      if (tm.data) setTeamMembers(tm.data);
      if (bl && bl.data) setBlogs(bl.data);
      if (tmpl && tmpl.data) setEmailTemplates(tmpl.data);
    } catch {
      toast.error("Failed to load dashboard data");
    }
  };


  const saveSettings = async (e) => {
    e.preventDefault();
    try {
      await api.put("/settings", settings);
      toast.success("Company settings & addresses updated live!");
      load();
    } catch {
      toast.error("Failed to update company settings");
    }
  };



  useEffect(() => {
    if (admin) load();
  }, [admin]);

  if (checking) return <div className="min-h-screen grid place-items-center text-neutral-400">Loading...</div>;
  if (!admin) return <Navigate to="/admin/login" replace />;

  const setStatus = async (id, status) => {
    await api.patch(`/contacts/${id}`, { status });
    toast.success("Lead status updated");
    load();
  };

  const removeLead = async (id) => {
    await api.delete(`/contacts/${id}`);
    toast.success("Lead deleted");
    load();
  };

  const setAppStatus = async (id, status) => {
    await api.patch(`/applications/${id}`, { status });
    toast.success("Application status updated");
    load();
  };

  const handleSaveTemplate = async (e) => {
    e.preventDefault();
    if (!editingTemplate) return;
    try {
      await api.put(`/email-templates/${editingTemplate.id}`, {
        subject: editingTemplate.subject,
        body: editingTemplate.body,
      });
      toast.success("Email template updated!");
      setEditingTemplate(null);
      load();
    } catch {
      toast.error("Failed to update email template");
    }
  };

  const markdownToHtml = (str) => {
    if (!str) return "";
    if (/<[a-z][\s\S]*>/i.test(str)) return str;

    let lines = str.split("\n");
    let inList = false;
    let html = [];

    lines.forEach((line) => {
      let l = line.trim();
      if (!l) {
        if (inList) { html.push("</ul>"); inList = false; }
        return;
      }

      l = l.replace(/!\[(.*?)\]\((.*?)\)/g, '<img src="$2" alt="$1" class="rounded-2xl max-h-[360px] w-full object-cover my-4" />');
      l = l.replace(/\[(.*?)\]\((.*?)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" class="text-[#FF5A1F] font-semibold underline">$1</a>');
      l = l.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
      l = l.replace(/\*(.*?)\*/g, '<em>$1</em>');

      if (l.startsWith("## ")) {
        if (inList) { html.push("</ul>"); inList = false; }
        html.push(`<h2>${l.replace(/^##\s*/, "")}</h2>`);
      } else if (l.startsWith("# ")) {
        if (inList) { html.push("</ul>"); inList = false; }
        html.push(`<h1>${l.replace(/^#\s*/, "")}</h1>`);
      } else if (l.startsWith("### ")) {
        if (inList) { html.push("</ul>"); inList = false; }
        html.push(`<h3>${l.replace(/^###\s*/, "")}</h3>`);
      } else if (l.startsWith("> ")) {
        if (inList) { html.push("</ul>"); inList = false; }
        html.push(`<blockquote>${l.replace(/^>\s*/, "")}</blockquote>`);
      } else if (l.startsWith("- ") || l.startsWith("* ")) {
        if (!inList) { html.push("<ul>"); inList = true; }
        html.push(`<li>${l.replace(/^[\*\-\•]\s*/, "")}</li>`);
      } else {
        if (inList) { html.push("</ul>"); inList = false; }
        html.push(`<p>${l}</p>`);
      }
    });

    if (inList) html.push("</ul>");
    return html.join("\n");
  };

  const handleResetTemplates = async () => {

    if (!window.confirm("Are you sure you want to reset all email templates to system defaults?")) return;
    try {
      const res = await api.post("/email-templates/reset");
      setEmailTemplates(res.data);
      toast.success("Email templates reset to defaults!");
    } catch {
      toast.error("Failed to reset email templates");
    }
  };

  const handleSendTestEmail = async (templateId) => {
    const targetEmail = window.prompt("Enter test email address:", "career@neuravolt.in");
    if (!targetEmail || !targetEmail.includes("@")) return;
    try {
      await api.post(`/email-templates/${templateId}/test-email`, { to_email: targetEmail.trim() });
      toast.success(`Test email sent successfully to ${targetEmail}! Please check inbox/spam.`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send test email. Check SMTP server settings.");
    }
  };

  const handleBulkSendConfirmation = async () => {
    if (!window.confirm(`Send Candidate Confirmation Email to all ${applications.length} applicants?`)) return;
    setSendingBulk(true);
    try {
      const res = await api.post("/applications/bulk-send-confirmation", {});
      toast.success(`Confirmation emails sent: ${res.data.sent_count} successful, ${res.data.failed_count} failed.`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send bulk confirmation emails.");
    } finally {
      setSendingBulk(false);
    }
  };

  const handleSendCandidateEmail = async (e) => {
    e.preventDefault();
    if (!sendEmailModal) return;
    setSendEmailModal((prev) => ({ ...prev, sending: true }));
    try {
      await api.post(`/applications/${sendEmailModal.app.id}/send-email`, {
        template_id: sendEmailModal.template_id,
        subject: sendEmailModal.subject,
        message: sendEmailModal.message,
        interview_datetime: sendEmailModal.interview_datetime,
        interview_location: sendEmailModal.interview_location,
      });

      toast.success(`Email sent successfully to ${sendEmailModal.app.name} (${sendEmailModal.app.email})`);
      setSendEmailModal(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send email. Check SMTP server settings.");
    } finally {
      setSendEmailModal((prev) => (prev ? { ...prev, sending: false } : null));
    }
  };





  const renderPreviewContent = (text) => {
    if (!text) return <p className="text-neutral-400 italic">No content written yet.</p>;
    const isHtml = /<[a-z][\s\S]*>/i.test(text);
    if (isHtml) {
      return <div className="space-y-3 font-sans text-neutral-700" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(text) }} />;
    }
    const blocks = text.split(/\n\s*\n/);
    return blocks.map((b, i) => {
      const trimmed = b.trim();
      if (trimmed.startsWith("## ")) return <h3 key={i} className="font-bold text-lg text-[#0B0B0B] mt-4 mb-2">{trimmed.replace(/^##\s*/, "")}</h3>;
      if (trimmed.startsWith("# ")) return <h2 key={i} className="font-bold text-xl text-[#0B0B0B] mt-5 mb-2">{trimmed.replace(/^#\s*/, "")}</h2>;
      if (trimmed.startsWith("### ")) return <h4 key={i} className="font-semibold text-base text-[#0B0B0B] mt-3 mb-1">{trimmed.replace(/^###\s*/, "")}</h4>;
      if (trimmed.startsWith("> ")) return <blockquote key={i} className="border-l-4 border-[#FF5A1F] pl-3 italic text-neutral-600 bg-[#FF5A1F]/5 py-1 my-2">{trimmed.replace(/^>\s*/, "")}</blockquote>;
      if (trimmed.startsWith("- ") || trimmed.startsWith("* ")) {
        return (
          <ul key={i} className="list-disc pl-5 space-y-1 text-sm text-neutral-700">
            {trimmed.split("\n").map((li, idx) => <li key={idx}>{li.replace(/^[\*\-\•]\s*/, "")}</li>)}
          </ul>
        );
      }
      return <p key={i} className="text-sm text-neutral-700 leading-relaxed">{trimmed}</p>;
    });
  };



  const removeApp = async (id) => {

    if (!window.confirm("Are you sure you want to delete this candidate application?")) return;
    await api.delete(`/applications/${id}`);
    toast.success("Application deleted");
    load();
  };


  // Projects CRUD
  const saveProject = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...projectForm,
        slug: projectForm.slug || projectForm.title.toLowerCase().replace(/[^a-z0-9]+/g, "-"),
        tech: typeof projectForm.tech === "string" ? projectForm.tech.split(",").map((t) => t.trim()).filter(Boolean) : projectForm.tech,
      };
      if (projectForm.id) {
        await api.put(`/projects/${projectForm.id}`, payload);
        toast.success("Project updated");
      } else {
        await api.post("/projects", payload);
        toast.success("Project created");
      }
      setProjectForm(null);
      load();
    } catch {
      toast.error("Failed to save project");
    }
  };

  const deleteProject = async (id) => {
    if (!window.confirm("Are you sure you want to delete this project?")) return;
    try {
      await api.delete(`/projects/${id}`);
      toast.success("Project deleted");
      load();
    } catch {
      toast.error("Failed to delete project");
    }
  };

  // Clients CRUD
  const saveClient = async (e) => {
    e.preventDefault();
    try {
      if (clientForm.id) {
        await api.put(`/clients/${clientForm.id}`, clientForm);
        toast.success("Client updated");
      } else {
        await api.post("/clients", clientForm);
        toast.success("Client created");
      }
      setClientForm(null);
      load();
    } catch {
      toast.error("Failed to save client");
    }
  };

  // Jobs CRUD
  const saveJob = async (e) => {
    e.preventDefault();
    try {
      if (jobForm.id) {
        await api.put(`/jobs/${jobForm.id}`, jobForm);
        toast.success("Job opening updated");
      } else {
        await api.post("/jobs", jobForm);
        toast.success("Job opening created");
      }
      setJobForm(null);
      load();
    } catch {
      toast.error("Failed to save job opening");
    }
  };

  const deleteJob = async (id) => {
    if (!window.confirm("Are you sure you want to delete this job opening?")) return;
    try {
      await api.delete(`/jobs/${id}`);
      toast.success("Job opening deleted");
      load();
    } catch {
      toast.error("Failed to delete job opening");
    }
  };

  // Team CRUD
  const saveTeamMember = async (e) => {
    e.preventDefault();
    try {
      if (teamForm.id) {
        await api.put(`/team/${teamForm.id}`, teamForm);
        toast.success("Team member updated");
      } else {
        await api.post("/team", teamForm);
        toast.success("Team member added");
      }
      setTeamForm(null);
      load();
    } catch {
      toast.error("Failed to save team member");
    }
  };

  const deleteTeamMember = async (id) => {
    if (!window.confirm("Are you sure you want to remove this team member?")) return;
    try {
      await api.delete(`/team/${id}`);
      toast.success("Team member deleted");
      load();
    } catch {
      toast.error("Failed to delete team member");
    }
  };

  return (
    <div className="flex h-screen w-screen overflow-hidden bg-[#F7F6F2] text-neutral-800">
      {/* LEFT SIDEBAR NAVIGATION (FIXED & COLLAPSIBLE) */}
      <aside className={`${sidebarCollapsed ? "w-20" : "w-72"} h-screen bg-white border-r border-black/10 flex flex-col shrink-0 z-30 shadow-sm transition-all duration-300`}>
        <div className="p-4 sm:p-5 flex flex-col h-full">
          {/* Logo & Brand Header with Collapse Toggle Button */}
          <div className={`flex flex-col ${sidebarCollapsed ? "items-center" : ""} mb-5 pb-4 border-b border-black/5 shrink-0`}>
            <div className={`flex items-center ${sidebarCollapsed ? "justify-center" : "justify-between"} w-full`}>
              <div className={`flex items-center gap-3 min-w-0 ${sidebarCollapsed ? "justify-center" : ""}`}>
                <img
                  src="/dark-fav.png"
                  alt="Neuravolt"
                  className="w-10 h-10 rounded-2xl shadow-md shadow-[#FF5A1F]/20 shrink-0 object-contain"
                />
                {!sidebarCollapsed && (
                  <div className="min-w-0 truncate">
                    <div className="font-display text-base font-bold text-[#0B0B0B] leading-tight truncate">Neuravolt</div>
                    <div className="text-[10px] font-bold text-[#FF5A1F] uppercase tracking-wider truncate">Admin Dashboard</div>
                  </div>
                )}
              </div>

              {/* Show collapse arrow inline only when expanded */}
              {!sidebarCollapsed && (
                <button
                  onClick={() => setSidebarCollapsed(true)}
                  className="p-2 rounded-xl text-neutral-400 hover:text-black hover:bg-neutral-100 transition-colors shrink-0"
                  title="Collapse Sidebar"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
              )}
            </div>

            {/* Show expand arrow below logo when collapsed */}
            {sidebarCollapsed && (
              <button
                onClick={() => setSidebarCollapsed(false)}
                className="mt-3 p-2 rounded-xl text-neutral-400 hover:text-black hover:bg-neutral-100 transition-colors"
                title="Expand Sidebar"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            )}
          </div>

          {/* Vertical Menu Navigation Items */}
          {!sidebarCollapsed && (
            <div className="text-[10px] font-bold uppercase tracking-wider text-neutral-400 mb-2 px-2 shrink-0">Main Navigation</div>
          )}

          <nav className="space-y-1.5 flex-1 pr-1">
            {[
              { key: "leads", label: "Client Sales Leads", icon: Inbox, count: leads.length },
              { key: "applications", label: "Career Applications", icon: Briefcase, count: applications.length },
              { key: "templates", label: "Email Templates", icon: Mail, count: emailTemplates.length },
              { key: "jobs", label: "Job Openings", icon: Target, count: jobs.length },
              { key: "team", label: "Leadership Team", icon: Users, count: teamMembers.length },
              { key: "blogs", label: "Blog Posts & News", icon: FileText, count: blogs.length },
              { key: "projects", label: "Portfolio Projects", icon: FolderGit2, count: projects.length },
              { key: "clients", label: "Client Brands & Logos", icon: Building2, count: clients.length },
              { key: "subscribers", label: "Newsletter List", icon: Send, count: subs.length },
              { key: "settings", label: "Company Info & Settings", icon: MapPin, count: null },
            ].map((t) => {
              const Icon = t.icon;
              const isActive = tab === t.key;
              return (
                <button
                  key={t.key}
                  data-testid={`tab-${t.key}`}
                  onClick={() => {
                    setTab(t.key);
                    if (t.key !== "blogs") setBlogForm(null);
                  }}
                  title={sidebarCollapsed ? t.label : ""}
                  className={`w-full group flex items-center ${sidebarCollapsed ? "justify-center px-2 py-3" : "justify-between px-3.5 py-3"} rounded-2xl text-xs sm:text-sm font-semibold transition-all duration-200 ${
                    isActive
                      ? "bg-[#FF5A1F] text-white shadow-lg shadow-[#FF5A1F]/25"
                      : "text-neutral-600 hover:text-[#0B0B0B] hover:bg-neutral-100"
                  }`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <Icon className={`w-4 h-4 shrink-0 transition-colors ${isActive ? "text-white" : "text-neutral-400 group-hover:text-[#FF5A1F]"}`} />
                    {!sidebarCollapsed && <span className="truncate">{t.label}</span>}
                  </div>
                  {!sidebarCollapsed && t.count !== null && (
                    <span
                      className={`px-2 py-0.5 rounded-full text-[10px] font-bold shrink-0 transition-colors ${
                        isActive
                          ? "bg-white text-[#FF5A1F]"
                          : "bg-neutral-100 text-neutral-500 group-hover:bg-[#FF5A1F]/10 group-hover:text-[#FF5A1F]"
                      }`}
                    >
                      {t.count}
                    </span>
                  )}
                </button>
              );
            })}
          </nav>

          {/* Bottom Admin Profile & Logout */}
          <div className="pt-4 border-t border-black/5 mt-auto shrink-0">
            <div className={`flex items-center ${sidebarCollapsed ? "justify-center p-2" : "justify-between p-3"} rounded-2xl bg-[#FAF9F6] border border-black/5`}>
              {!sidebarCollapsed && (
                <div className="min-w-0 pr-2">
                  <div className="text-xs font-bold text-[#0B0B0B] truncate">Administrator</div>
                  <div className="text-[11px] text-neutral-400 truncate">{admin.email}</div>
                </div>
              )}
              <button
                data-testid="logout-btn"
                onClick={() => {
                  logout();
                  navigate("/");
                }}
                className="p-2 rounded-xl border border-black/10 bg-white text-neutral-600 hover:text-red-500 hover:border-red-200 hover:bg-red-50 transition-colors shadow-sm shrink-0"
                title="Logout"
              >
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </aside>

      {/* RIGHT MAIN CONTENT AREA - INDEPENDENT SCROLL */}
      <main className="flex-1 min-w-0 h-screen overflow-y-auto p-6 sm:p-10">

        {/* Main Content Title Bar */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#0B0B0B] capitalize">
              {tab === "leads" && "Client Sales Leads"}
              {tab === "applications" && "Career Applications"}
              {tab === "templates" && "Email Templates Manager"}
              {tab === "jobs" && "Job Openings"}
              {tab === "team" && "Leadership & Team Members"}
              {tab === "blogs" && (blogForm ? (blogForm.id ? "Edit Article" : "Create New Article") : "Blog Posts & Insights")}
              {tab === "projects" && "Portfolio Projects"}
              {tab === "clients" && "Client Brands & Logos"}
              {tab === "subscribers" && "Newsletter Subscribers"}
              {tab === "settings" && "Company Addresses & Info"}
            </h1>
            <p className="text-neutral-500 text-xs mt-1">Neuravolt Technology Enterprise Management Platform</p>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={load}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white border border-black/10 text-xs font-semibold text-neutral-700 hover:border-[#FF5A1F] hover:text-[#FF5A1F] transition-colors shadow-sm"
            >
              <RefreshCw className="w-3.5 h-3.5" /> Refresh Data
            </button>
          </div>
        </div>

        {/* Overview Stat Cards Row */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard icon={Inbox} label="Client Sales Leads" value={leads.length} />
          <StatCard icon={Users} label="Job Applications" value={applications.length} />
          <StatCard icon={FolderGit2} label="Portfolio Projects" value={projects.length} />
          <StatCard icon={Globe} label="Client Brands" value={clients.length} />
        </div>





        {/* CLIENT LEADS TAB */}
        {tab === "leads" && (
          <div className="space-y-3" data-testid="leads-list">
            {leads.length === 0 && <p className="text-neutral-500">No client sales leads submitted yet.</p>}
            {leads.map((l) => (
              <div key={l.id} className="bg-white border border-black/5 rounded-2xl p-5 shadow-sm" data-testid={`lead-${l.id}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-3">
                      <h3 className="font-heading font-semibold text-lg">{l.name}</h3>
                      <span className={`text-xs px-2.5 py-0.5 rounded-full font-medium ${l.status === "new" ? "bg-[#FF5A1F] text-white" : "bg-neutral-100 text-neutral-600"}`}>
                        {l.status}
                      </span>
                    </div>
                    <p className="text-sm text-neutral-500 mt-1">{l.email} · {l.company || "No company"} · {l.phone || "No phone"}</p>
                    <p className="text-sm text-neutral-400 mt-0.5">{l.service || "General"} · {l.budget || "Flexible"}</p>
                    <p className="text-neutral-700 mt-3 max-w-2xl bg-neutral-50 p-3 rounded-xl border border-black/5 text-sm">{l.message}</p>
                  </div>
                  <div className="flex gap-2">
                    {l.status === "new" && (
                      <button onClick={() => setStatus(l.id, "contacted")} className="w-9 h-9 rounded-lg bg-neutral-100 grid place-items-center hover:bg-[#FF5A1F] hover:text-white transition-colors" title="Mark Contacted">
                        <Check className="w-4 h-4" />
                      </button>
                    )}
                    <button onClick={() => removeLead(l.id)} className="w-9 h-9 rounded-lg bg-neutral-100 grid place-items-center hover:bg-red-500 hover:text-white transition-colors" title="Delete">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* CAREER APPLICATIONS TAB */}
        {tab === "applications" && (
          <div className="space-y-4" data-testid="applications-list">
            {applications.length > 0 && (
              <div className="flex flex-wrap items-center justify-between gap-4 bg-emerald-50 border border-emerald-200 rounded-2xl p-5 shadow-sm">
                <div>
                  <h3 className="font-bold text-emerald-900 text-sm flex items-center gap-2">
                    <Mail className="w-4 h-4 text-emerald-600" />
                    Past Applicants Email Notification ({applications.length} Total Candidates)
                  </h3>
                  <p className="text-xs text-emerald-700 mt-1">
                    Send official Candidate Confirmation emails to existing applicants who applied before the email system was enabled.
                  </p>
                </div>
                <button
                  onClick={handleBulkSendConfirmation}
                  disabled={sendingBulk}
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-emerald-600 text-white text-xs font-bold hover:bg-emerald-700 transition-colors shadow-sm disabled:opacity-50"
                >
                  <Send className="w-3.5 h-3.5" />
                  {sendingBulk ? "Sending Emails..." : `Send Confirmation to All ${applications.length} Candidates`}
                </button>
              </div>
            )}

            {applications.length === 0 && <p className="text-neutral-500">No career job applications submitted yet.</p>}
            {applications.map((app) => (

              <div key={app.id} className="bg-white border border-black/5 rounded-2xl p-6 shadow-sm">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-3 flex-wrap">
                      <h3 className="font-heading font-bold text-xl text-[#0B0B0B]">{app.name}</h3>
                      <span className="px-3 py-1 rounded-full bg-[#FF5A1F]/10 text-[#FF5A1F] text-xs font-bold border border-[#FF5A1F]/20">
                        {app.role}
                      </span>
                      <span className={`text-xs px-2.5 py-0.5 rounded-full font-medium ${app.status === "new" ? "bg-emerald-500 text-white" : "bg-neutral-100 text-neutral-600"}`}>
                        {app.status}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-neutral-500 mt-2 flex-wrap">
                      <span>📧 {app.email}</span>
                      <span>📞 {app.phone || "No phone"}</span>
                      <span>📅 {new Date(app.created_at).toLocaleDateString()}</span>
                    </div>

                    {/* Resume & Portfolio Links */}
                    <div className="flex items-center gap-3 mt-3 flex-wrap">
                      {app.resume_url && (
                        <a
                          href={app.resume_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-50 text-emerald-700 border border-emerald-200 text-xs font-semibold hover:bg-emerald-100 transition-colors"
                        >
                          📄 View / Download Resume
                        </a>
                      )}
                      {app.portfolio_url && (
                        <a
                          href={app.portfolio_url.startsWith("http") ? app.portfolio_url : `https://${app.portfolio_url}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-black/5 text-neutral-800 text-xs font-semibold hover:bg-black/10 transition-colors"
                        >
                          🔗 LinkedIn / Portfolio Link
                        </a>
                      )}
                    </div>

                    {/* Cover Note / Message */}
                    {app.message && (
                      <p className="text-neutral-700 mt-3 max-w-2xl bg-[#FAF9F6] p-3.5 rounded-xl border border-black/5 text-sm">
                        <span className="text-xs font-bold text-neutral-400 block mb-1">Cover Note:</span>
                        {app.message}
                      </p>
                    )}
                  </div>

                  <div className="flex items-center gap-2 flex-wrap">
                    <select
                      value={app.status}
                      onChange={(e) => setAppStatus(app.id, e.target.value)}
                      className="px-3 py-1.5 rounded-lg border border-black/10 text-xs font-semibold text-neutral-700 focus:outline-none focus:border-[#FF5A1F] bg-white cursor-pointer"
                    >
                      <option value="new">new</option>
                      <option value="reviewed">reviewed</option>
                      <option value="shortlisted">shortlisted</option>
                      <option value="interview_scheduled">interview_scheduled</option>
                      <option value="rejected">rejected</option>
                      <option value="hired">hired</option>
                    </select>

                    <button
                      onClick={() =>
                        setSendEmailModal({
                          app,
                          template_id: "candidate_ack",
                          subject: `Application Received: ${app.role} at Neuravolt`,
                          message: "",
                          sending: false,
                        })
                      }
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-50 text-emerald-700 border border-emerald-200 text-xs font-semibold hover:bg-emerald-100 transition-colors"
                      title="Send Application Confirmation email to candidate"
                    >
                      <Check className="w-3.5 h-3.5" />
                      Send Confirmation
                    </button>

                    <button
                      onClick={() =>
                        setSendEmailModal({
                          app,
                          template_id: "candidate_shortlisted",
                          subject: `Next Steps: Application for ${app.role} at Neuravolt`,
                          message: "",
                          interview_datetime: "2:00 PM Onwards",
                          interview_location: "Neuravolt Technology • E Block, 511, Amity Innovation Center, Gwalior, MP - 474020, India",
                          sending: false,
                        })
                      }


                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#FF5A1F]/10 text-[#FF5A1F] text-xs font-semibold hover:bg-[#FF5A1F] hover:text-white transition-colors"
                      title="Send custom or templated email to candidate"
                    >
                      <Mail className="w-3.5 h-3.5" />
                      Custom Email
                    </button>

                    <button
                      onClick={() => removeApp(app.id)}
                      className="w-8 h-8 rounded-lg bg-neutral-100 grid place-items-center hover:bg-red-500 hover:text-white transition-colors"
                      title="Delete Candidate"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>

                </div>
              </div>
            ))}
          </div>
        )}

        {/* EMAIL TEMPLATES TAB */}
        {tab === "templates" && (
          <div className="space-y-6" data-testid="templates-tab">
            <div className="flex flex-wrap items-center justify-between gap-4 bg-white border border-black/5 rounded-2xl p-6 shadow-sm">
              <div>
                <h2 className="font-heading font-bold text-2xl text-[#0B0B0B]">Career Email Templates</h2>
                <p className="text-neutral-500 text-sm mt-1">
                  Manage & edit HTML email templates sent to candidates and HR. Insert dynamic variables like <code className="bg-neutral-100 px-1.5 py-0.5 rounded text-xs font-mono text-[#FF5A1F]">&#123;&#123;name&#125;&#125;</code> and <code className="bg-neutral-100 px-1.5 py-0.5 rounded text-xs font-mono text-[#FF5A1F]">&#123;&#123;role&#125;&#125;</code>.
                </p>
              </div>
              <button
                onClick={handleResetTemplates}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-neutral-100 text-neutral-700 text-xs font-bold hover:bg-neutral-200 transition-colors"
                title="Reset all templates to default"
              >
                <RefreshCw className="w-3.5 h-3.5" />
                Reset Defaults
              </button>
            </div>

            {/* Template Cards Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {emailTemplates.map((tmpl) => (
                <div key={tmpl.id} className="bg-white border border-black/5 rounded-2xl p-6 shadow-sm flex flex-col justify-between">
                  <div>
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <span className="px-2.5 py-0.5 rounded-full bg-[#FF5A1F]/10 text-[#FF5A1F] text-[11px] font-bold uppercase tracking-wider">
                          {tmpl.event}
                        </span>
                        <h3 className="font-bold text-lg text-[#0B0B0B] mt-2">{tmpl.name}</h3>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleSendTestEmail(tmpl.id)}
                          className="px-2.5 py-1.5 rounded-xl bg-neutral-100 text-neutral-700 text-xs font-semibold hover:bg-neutral-200 transition-colors"
                          title="Send test email for this template"
                        >
                          🧪 Test Email
                        </button>
                        <button
                          onClick={() => setEditingTemplate(tmpl)}
                          className="p-2 rounded-xl bg-neutral-100 text-neutral-700 hover:bg-[#FF5A1F] hover:text-white transition-colors"
                          title="Edit Template"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>


                    <p className="text-xs text-neutral-500 mt-1">{tmpl.description}</p>

                    <div className="mt-4 bg-[#FAF9F6] p-3.5 rounded-xl border border-black/5 space-y-2">
                      <div className="text-xs font-semibold text-neutral-700">
                        <span className="text-neutral-400">Subject:</span> {tmpl.subject}
                      </div>
                      <div className="text-xs text-neutral-600 line-clamp-3 font-mono bg-white p-2.5 rounded-lg border border-black/5">
                        {tmpl.body}
                      </div>
                    </div>

                    {/* Variable Badges */}
                    <div className="mt-3 flex flex-wrap gap-1.5">
                      <span className="text-[11px] text-neutral-400 font-medium self-center mr-1">Variables:</span>
                      {tmpl.variables?.map((v) => (
                        <span key={v} className="px-2 py-0.5 rounded bg-neutral-100 text-neutral-600 font-mono text-[11px]">
                          &#123;&#123;{v}&#125;&#125;
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* EDIT TEMPLATE MODAL */}
        {editingTemplate && (
          <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto">
            <div className="bg-white rounded-3xl p-6 sm:p-8 max-w-3xl w-full shadow-2xl space-y-5 my-8">
              <div className="flex items-center justify-between border-b border-black/5 pb-4">
                <div>
                  <span className="px-2.5 py-0.5 rounded-full bg-[#FF5A1F]/10 text-[#FF5A1F] text-[11px] font-bold uppercase tracking-wider">
                    {editingTemplate.event}
                  </span>
                  <h3 className="font-bold text-xl text-[#0B0B0B] mt-1">{editingTemplate.name}</h3>
                </div>
                <button onClick={() => setEditingTemplate(null)} className="p-2 rounded-full hover:bg-neutral-100">
                  <X className="w-5 h-5 text-neutral-500" />
                </button>
              </div>

              {/* Dynamic Variables helper guide */}
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-xs text-amber-900">
                <div className="font-bold mb-1 flex items-center gap-1.5">
                  <span>💡 Available Dynamic Variables:</span>
                </div>
                <div className="flex flex-wrap gap-2 mt-2">
                  {["name", "role", "email", "phone", "date", "company", "custom_note", "resume_link", "portfolio_link", "message"].map((v) => (
                    <button
                      key={v}
                      type="button"
                      onClick={() => setEditingTemplate((prev) => ({ ...prev, body: prev.body + ` {{${v}}}` }))}
                      className="px-2 py-1 rounded bg-white border border-amber-300 font-mono text-[11px] text-amber-900 hover:bg-amber-100 transition-colors"
                      title="Click to append to template body"
                    >
                      + &#123;&#123;{v}&#125;&#125;
                    </button>
                  ))}
                </div>
              </div>

              <form onSubmit={handleSaveTemplate} className="space-y-4">
                <div>
                  <label className="block text-xs font-bold uppercase text-neutral-500 mb-1">Email Subject Line</label>
                  <input
                    type="text"
                    required
                    value={editingTemplate.subject}
                    onChange={(e) => setEditingTemplate({ ...editingTemplate, subject: e.target.value })}
                    className="w-full px-4 py-2.5 rounded-xl border border-black/10 text-sm focus:outline-none focus:border-[#FF5A1F]"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold uppercase text-neutral-500 mb-1">Email Body Content (HTML / Text with variables)</label>
                  <textarea
                    rows={12}
                    required
                    value={editingTemplate.body}
                    onChange={(e) => setEditingTemplate({ ...editingTemplate, body: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl border border-black/10 text-xs font-mono focus:outline-none focus:border-[#FF5A1F] leading-relaxed"
                  />
                </div>

                <div className="flex justify-end gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => handleSendTestEmail(editingTemplate.id)}
                    className="px-4 py-2.5 rounded-xl bg-neutral-100 text-sm font-semibold text-neutral-700 hover:bg-neutral-200"
                  >
                    🧪 Send Test Email
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditingTemplate(null)}
                    className="px-5 py-2.5 rounded-xl bg-neutral-100 text-sm font-semibold text-neutral-600 hover:bg-neutral-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-2.5 rounded-xl bg-[#FF5A1F] text-white text-sm font-semibold hover:bg-[#e04f1a] shadow-md"
                  >
                    Save Template
                  </button>
                </div>

              </form>
            </div>
          </div>
        )}

        {/* SEND EMAIL TO CANDIDATE MODAL */}
        {sendEmailModal && (
          <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto">
            <div className="bg-white rounded-3xl p-6 sm:p-8 max-w-2xl w-full shadow-2xl space-y-5 my-8">
              <div className="flex items-center justify-between border-b border-black/5 pb-4">
                <div>
                  <span className="text-xs font-bold uppercase tracking-wider text-[#FF5A1F]">Send Email Notification</span>
                  <h3 className="font-bold text-xl text-[#0B0B0B]">To: {sendEmailModal.app.name} ({sendEmailModal.app.email})</h3>
                </div>
                <button onClick={() => setSendEmailModal(null)} className="p-2 rounded-full hover:bg-neutral-100">
                  <X className="w-5 h-5 text-neutral-500" />
                </button>
              </div>

              <form onSubmit={handleSendCandidateEmail} className="space-y-4">
                <div>
                  <label className="block text-xs font-bold uppercase text-neutral-500 mb-1">Select Template Preset</label>
                  <select
                    value={sendEmailModal.template_id}
                    onChange={(e) => {
                      const tid = e.target.value;
                      const found = emailTemplates.find((t) => t.id === tid);
                      setSendEmailModal((prev) => ({
                        ...prev,
                        template_id: tid,
                        subject: found ? found.subject : prev.subject,
                        interview_datetime: prev.interview_datetime || "2:00 PM Onwards",
                        interview_location: prev.interview_location || "Neuravolt Technology • E Block, 511, Amity Innovation Center, Gwalior, MP - 474020, India",
                      }));
                    }}
                    className="w-full px-4 py-2.5 rounded-xl border border-black/10 text-sm focus:outline-none focus:border-[#FF5A1F]"
                  >

                    <option value="">Custom Email / Freeform</option>
                    {emailTemplates.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name} ({t.subject})
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-bold uppercase text-neutral-500 mb-1">Subject Line</label>
                  <input
                    type="text"
                    required
                    value={sendEmailModal.subject}
                    onChange={(e) => setSendEmailModal({ ...sendEmailModal, subject: e.target.value })}
                    className="w-full px-4 py-2.5 rounded-xl border border-black/10 text-sm focus:outline-none focus:border-[#FF5A1F]"
                  />
                </div>


                {sendEmailModal.template_id === "candidate_shortlisted" && (
                  <div className="grid sm:grid-cols-2 gap-4 bg-[#FF5A1F]/5 p-4 rounded-2xl border border-[#FF5A1F]/20">
                    <div>
                      <label className="block text-xs font-bold uppercase text-[#FF5A1F] mb-1">Interview Date & Time</label>
                      <input
                        type="text"
                        placeholder="e.g. 2:00 PM Onwards"
                        value={sendEmailModal.interview_datetime || ""}
                        onChange={(e) => setSendEmailModal({ ...sendEmailModal, interview_datetime: e.target.value })}
                        className="w-full px-3.5 py-2 bg-white rounded-xl border border-black/10 text-xs focus:outline-none focus:border-[#FF5A1F]"
                      />

                    </div>
                    <div>
                      <label className="block text-xs font-bold uppercase text-[#FF5A1F] mb-1">Location / Google Meet Link</label>
                      <input
                        type="text"
                        placeholder="e.g. Neuravolt Technology • E Block, 511, Amity Innovation Center, Gwalior, MP - 474020, India"
                        value={sendEmailModal.interview_location || ""}
                        onChange={(e) => setSendEmailModal({ ...sendEmailModal, interview_location: e.target.value })}
                        className="w-full px-3.5 py-2 bg-white rounded-xl border border-black/10 text-xs focus:outline-none focus:border-[#FF5A1F]"
                      />
                    </div>

                  </div>
                )}


                <div>
                  <label className="block text-xs font-bold uppercase text-neutral-500 mb-1">
                    {sendEmailModal.template_id ? "Custom Note / Message insert (replaces {{custom_note}})" : "Email Message Content"}
                  </label>
                  <textarea
                    rows={6}
                    required
                    placeholder="Type your message or notes here..."
                    value={sendEmailModal.message}
                    onChange={(e) => setSendEmailModal({ ...sendEmailModal, message: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl border border-black/10 text-sm focus:outline-none focus:border-[#FF5A1F]"
                  />
                </div>

                <div className="flex justify-end gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => setSendEmailModal(null)}
                    className="px-5 py-2.5 rounded-xl bg-neutral-100 text-sm font-semibold text-neutral-600 hover:bg-neutral-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={sendEmailModal.sending}
                    className="inline-flex items-center gap-2 px-6 py-2.5 rounded-xl bg-[#FF5A1F] text-white text-sm font-semibold hover:bg-[#e04f1a] shadow-md disabled:opacity-50"
                  >
                    <Send className="w-4 h-4" />
                    {sendEmailModal.sending ? "Sending..." : "Send Email"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}



        {/* TEAM TAB (Full CRUD + Photo Upload) */}
        {tab === "team" && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="font-display text-xl font-bold">Manage Leadership & Team Members</h2>
                <p className="text-neutral-500 text-xs mt-1">Changes here update the Team / Leadership section on the About Us page live.</p>
              </div>
              <button
                onClick={() =>
                  setTeamForm({
                    name: "",
                    role: "",
                    initials: "",
                    img: "",
                    bio: "",
                  })
                }
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#FF5A1F] text-white text-sm font-semibold hover:bg-[#ff6d38] transition-all shadow-md"
              >
                <Plus className="w-4 h-4" /> Add Team Member
              </button>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
              {teamMembers.map((m) => (
                <div key={m.id || m.name} className="bg-white border border-black/10 rounded-2xl p-5 shadow-sm flex flex-col justify-between text-center">
                  <div>
                    {m.img ? (
                      <div className="w-20 h-20 rounded-2xl mx-auto mb-3 overflow-hidden border border-black/10 shadow-sm">
                        <img src={fixImgUrl(m.img)} alt={m.name} className="w-full h-full object-cover" />
                      </div>
                    ) : (
                      <div className="w-20 h-20 rounded-2xl mx-auto mb-3 grid place-items-center bg-gradient-to-br from-[#FF5A1F] to-[#7a2b00] text-white font-display text-xl font-bold shadow-md shadow-[#FF5A1F]/20">
                        {m.initials || (m.name ? m.name.substring(0, 2).toUpperCase() : "NV")}
                      </div>
                    )}
                    <h3 className="font-heading font-bold text-base text-neutral-900">{m.name}</h3>
                    <p className="text-[#FF5A1F] text-xs font-semibold mt-0.5">{m.role}</p>
                    {m.bio && <p className="text-neutral-500 text-xs mt-2 line-clamp-2">{m.bio}</p>}
                  </div>

                  <div className="flex items-center justify-center gap-2 pt-4 border-t border-black/5 mt-4">
                    <button
                      onClick={() => setTeamForm(m)}
                      className="px-3 py-1.5 rounded-lg bg-neutral-100 text-neutral-700 text-xs font-semibold inline-flex items-center gap-1 hover:bg-[#FF5A1F] hover:text-white transition-all"
                    >
                      <Edit2 className="w-3.5 h-3.5" /> Edit
                    </button>
                    <button
                      onClick={() => deleteTeamMember(m.id)}
                      className="px-3 py-1.5 rounded-lg bg-red-50 text-red-600 text-xs font-semibold inline-flex items-center gap-1 hover:bg-red-500 hover:text-white transition-all"
                    >
                      <Trash2 className="w-3.5 h-3.5" /> Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}


        {/* BLOG MANAGEMENT TAB */}
        {tab === "blogs" && (
          <div>
            {!blogForm ? (
              /* BLOG LISTING VIEW */
              <div>
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="font-display text-xl font-bold">Blog Posts & Insights</h2>
                    <p className="text-neutral-500 text-xs mt-1">Manage articles, updates, and company news shown on neuravolt.in/blog</p>
                  </div>
                  <button
                    onClick={() => setBlogForm({ title: "", slug: "", category: "AI", read: "5 min", excerpt: "", img: "", content: "", published: true })}
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#FF5A1F] text-white font-semibold text-sm hover:bg-[#ff6d38] transition-colors shadow-md"
                  >
                    <Plus className="w-4 h-4" /> New Article
                  </button>
                </div>

                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {blogs.length === 0 && <p className="text-neutral-400 col-span-3 py-12 text-center">No blog posts published yet. Click "New Article" to create your first post.</p>}
                  {blogs.map((b) => (
                    <div key={b.id} className="bg-white border border-black/5 rounded-2xl overflow-hidden shadow-sm flex flex-col group hover:shadow-md transition-shadow">
                      {b.img ? (
                        <div className="h-40 overflow-hidden bg-neutral-100">
                          <img src={fixImgUrl(b.img)} alt={b.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                        </div>
                      ) : (
                        <div className="h-40 bg-neutral-100 grid place-items-center"><FileText className="w-10 h-10 text-neutral-300" /></div>
                      )}
                      <div className="p-5 flex flex-col flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-[10px] font-bold uppercase tracking-wider text-[#FF5A1F] bg-[#FF5A1F]/10 px-2.5 py-0.5 rounded-full">{b.category}</span>
                          <span className="text-[10px] text-neutral-400">{b.read}</span>
                          {!b.published && <span className="text-[10px] font-bold text-amber-500 bg-amber-50 px-2 py-0.5 rounded-full">Draft</span>}
                        </div>
                        <h3 className="font-semibold text-base text-[#0B0B0B] leading-snug mb-2 line-clamp-2">{b.title}</h3>
                        <p className="text-neutral-500 text-xs line-clamp-2 flex-1">{b.excerpt}</p>
                        <div className="flex gap-2 mt-5 pt-4 border-t border-black/5">
                          <button
                            onClick={() => setBlogForm({ ...b, content: markdownToHtml(b.content) })}
                            className="flex-1 inline-flex items-center justify-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-xl border border-black/10 hover:border-[#FF5A1F] hover:text-[#FF5A1F] transition-colors"
                          >
                            <Edit2 className="w-3.5 h-3.5" /> Edit Article
                          </button>
                          <button
                            onClick={async () => { if (!window.confirm("Delete this blog post?")) return; await api.delete(`/blogs/${b.id}`); toast.success("Post deleted"); load(); }}
                            className="inline-flex items-center justify-center gap-1 text-xs font-semibold px-3 py-2 rounded-xl border border-red-200 text-red-500 hover:bg-red-50 transition-colors"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              /* DEDICATED FULL-PAGE ARTICLE WORKSPACE */
              <div className="bg-white rounded-3xl border border-black/10 shadow-lg overflow-hidden">
                {/* Header Bar */}
                <div className="flex items-center justify-between p-6 border-b border-black/10 bg-[#FAF9F6] sticky top-0 z-20">
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => setBlogForm(null)}
                      className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-white border border-black/10 text-xs font-semibold text-neutral-700 hover:border-[#FF5A1F] hover:text-[#FF5A1F] transition-colors shadow-sm"
                    >
                      ← Back to All Articles
                    </button>
                    <div>
                      <h2 className="font-display text-lg font-bold text-[#0B0B0B]">
                        {blogForm.id ? "Edit Article" : "Create New Article"}
                      </h2>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => setBlogForm(null)}
                      className="px-5 py-2.5 rounded-xl border border-black/10 text-neutral-600 font-semibold text-xs hover:bg-neutral-100 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={async (e) => {
                        e.preventDefault();
                        if (!blogForm.title) { toast.error("Please enter an article title"); return; }
                        try {
                          if (blogForm.id) {
                            await api.put(`/blogs/${blogForm.id}`, blogForm);
                            toast.success("Blog post updated!");
                          } else {
                            await api.post("/blogs", blogForm);
                            toast.success("Blog post published!");
                          }
                          setBlogForm(null);
                          load();
                        } catch { toast.error("Failed to save post"); }
                      }}
                      className="inline-flex items-center gap-2 px-6 py-2.5 rounded-xl bg-[#FF5A1F] text-white text-xs font-bold hover:bg-[#ff6d38] transition-colors shadow-md"
                    >
                      <Check className="w-4 h-4" /> {blogForm.id ? "Update Post" : "Publish Post"}
                    </button>
                  </div>
                </div>

                {/* Form Content Grid */}
                <form
                  onSubmit={async (e) => {
                    e.preventDefault();
                    try {
                      if (blogForm.id) {
                        await api.put(`/blogs/${blogForm.id}`, blogForm);
                        toast.success("Blog post updated!");
                      } else {
                        await api.post("/blogs", blogForm);
                        toast.success("Blog post published!");
                      }
                      setBlogForm(null);
                      load();
                    } catch { toast.error("Failed to save post"); }
                  }}
                  className="p-6 sm:p-8 grid grid-cols-1 lg:grid-cols-3 gap-8"
                >
                  {/* Left Column (Main Article & Google Docs Editor) */}
                  <div className="lg:col-span-2 space-y-6">
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider text-neutral-600 mb-1.5">Article Title *</label>
                      <input
                        required
                        value={blogForm.title}
                        onChange={(e) => setBlogForm({ ...blogForm, title: e.target.value })}
                        className="w-full bg-[#FAF9F6] border border-black/10 rounded-2xl px-5 py-4 text-lg font-bold outline-none focus:border-[#FF5A1F] focus:bg-white transition-colors"
                        placeholder="Enter article title here..."
                      />
                    </div>

                    {/* Google Docs Editor */}
                    <div className="border border-black/10 rounded-2xl overflow-hidden bg-white shadow-sm">
                      <div className="flex items-center justify-between p-3.5 bg-[#FAF9F6] border-b border-black/10">
                        <span className="text-xs font-bold uppercase tracking-wider text-neutral-600">
                          📝 Article Body (Google Docs / Medium Editor)
                        </span>
                        <div className="flex items-center gap-1 bg-white p-1 rounded-xl border border-black/10 text-xs font-semibold">
                          <button
                            type="button"
                            onClick={() => setBlogEditorTab("visual")}
                            className={`px-3 py-1 rounded-lg transition-colors ${blogEditorTab === "visual" ? "bg-[#FF5A1F] text-white" : "text-neutral-600 hover:text-black"}`}
                          >
                            📄 Google Docs Editor
                          </button>
                          <button
                            type="button"
                            onClick={() => setBlogEditorTab("preview")}
                            className={`px-3 py-1 rounded-lg transition-colors ${blogEditorTab === "preview" ? "bg-[#FF5A1F] text-white" : "text-neutral-600 hover:text-black"}`}
                          >
                            👁️ Live Preview
                          </button>
                        </div>
                      </div>

                      {blogEditorTab === "visual" ? (
                        <div className="p-2 bg-white min-h-[420px]">
                          <ReactQuill
                            ref={quillRef}
                            theme="snow"
                            value={blogForm.content || ""}
                            onChange={(content, delta, source, editor) => {
                              const textOnly = editor.getText();
                              const words = textOnly.trim().split(/\s+/).filter(Boolean).length;
                              const readMin = Math.max(1, Math.ceil(words / 200));
                              setBlogForm((prev) => (prev ? { ...prev, content, read: `${readMin} min` } : null));
                            }}
                            modules={quillModules}
                            formats={quillFormats}
                            placeholder="Write your article here like Google Docs... Add headings, bold text, lists, and images directly."
                            className="min-h-[380px]"
                          />
                        </div>
                      ) : (
                        <div className="p-6 bg-white min-h-[420px] max-h-[600px] overflow-y-auto space-y-4">
                          <div className="text-xs text-[#FF5A1F] font-bold uppercase tracking-wider">Live Website View • {blogForm.category} ({blogForm.read})</div>
                          <h2 className="text-2xl font-bold font-display text-[#0B0B0B]">{blogForm.title || "Article Title Preview"}</h2>
                          {blogForm.excerpt && <p className="text-base text-neutral-600 font-medium italic border-b border-black/5 pb-4">{blogForm.excerpt}</p>}
                          <div
                            className="prose-nv space-y-4 text-sm text-neutral-700 leading-relaxed [&_h2]:text-xl [&_h2]:font-bold [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_img]:rounded-xl [&_img]:max-h-80 [&_img]:w-full [&_img]:object-cover"
                            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(blogForm.content || "") }}
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Right Column (Publish & SEO Sidebar) */}
                  <div className="space-y-6">
                    {/* Publishing Card */}
                    <div className="bg-[#FAF9F6] p-5 rounded-2xl border border-black/10 space-y-4">
                      <div className="text-xs font-bold uppercase tracking-wider text-[#FF5A1F] flex items-center gap-2">
                        ⚙️ Article Settings
                      </div>

                      <div>
                        <label className="block text-xs font-bold uppercase tracking-wider text-neutral-600 mb-1">URL Slug</label>
                        <input
                          value={blogForm.slug || ""}
                          onChange={(e) => setBlogForm({ ...blogForm, slug: e.target.value })}
                          className="w-full bg-white border border-black/10 rounded-xl px-3.5 py-2.5 text-xs outline-none focus:border-[#FF5A1F]"
                          placeholder="e.g. ai-first-product-strategy (auto if empty)"
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-bold uppercase tracking-wider text-neutral-600 mb-1">Category</label>
                          <input
                            value={blogForm.category}
                            onChange={(e) => setBlogForm({ ...blogForm, category: e.target.value })}
                            className="w-full bg-white border border-black/10 rounded-xl px-3.5 py-2.5 text-xs outline-none focus:border-[#FF5A1F]"
                            placeholder="AI, Cloud, Web..."
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-bold uppercase tracking-wider text-neutral-600 mb-1">Read Time</label>
                          <input
                            value={blogForm.read}
                            onChange={(e) => setBlogForm({ ...blogForm, read: e.target.value })}
                            className="w-full bg-white border border-black/10 rounded-xl px-3.5 py-2.5 text-xs outline-none focus:border-[#FF5A1F]"
                            placeholder="5 min"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-xs font-bold uppercase tracking-wider text-neutral-600 mb-1">Excerpt / Summary</label>
                        <textarea
                          rows={3}
                          value={blogForm.excerpt}
                          onChange={(e) => setBlogForm({ ...blogForm, excerpt: e.target.value })}
                          className="w-full bg-white border border-black/10 rounded-xl px-3.5 py-2.5 text-xs outline-none focus:border-[#FF5A1F] resize-none"
                          placeholder="Short summary shown on blog listing..."
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-bold uppercase tracking-wider text-neutral-600 mb-1">Cover Image</label>
                        <div className="flex gap-2 items-start">
                          <input
                            type="text"
                            value={blogForm.img}
                            onChange={(e) => setBlogForm({ ...blogForm, img: e.target.value })}
                            className="flex-1 bg-white border border-black/10 rounded-xl px-3.5 py-2.5 text-xs outline-none focus:border-[#FF5A1F]"
                            placeholder="Paste image URL or upload"
                          />
                          <label className="relative cursor-pointer flex-shrink-0">
                            <input
                              type="file"
                              accept="image/*"
                              className="sr-only"
                              onChange={async (e) => {
                                const file = e.target.files?.[0]; if (!file) return;
                                setBlogImgUploading(true);
                                try {
                                  const fd = new FormData(); fd.append("file", file);
                                  const r = await api.post("/upload/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
                                  setBlogForm({ ...blogForm, img: r.data.url });
                                } catch { toast.error("Upload failed"); } finally { setBlogImgUploading(false); }
                              }}
                            />
                            <span className="inline-flex items-center px-3 py-2.5 rounded-xl bg-[#FF5A1F] text-white text-xs font-semibold whitespace-nowrap">
                              {blogImgUploading ? "..." : "Upload"}
                            </span>
                          </label>
                        </div>
                        {blogForm.img && (
                          <div className="mt-3 h-28 w-full rounded-xl overflow-hidden bg-neutral-100 border border-black/10">
                            <img src={fixImgUrl(blogForm.img)} alt="Preview" className="w-full h-full object-cover" />
                          </div>
                        )}
                      </div>

                      <div className="pt-2 border-t border-black/5">
                        <label className="flex items-center gap-2 cursor-pointer select-none">
                          <input
                            type="checkbox"
                            checked={blogForm.published}
                            onChange={(e) => setBlogForm({ ...blogForm, published: e.target.checked })}
                            className="w-4 h-4 accent-[#FF5A1F]"
                          />
                          <span className="text-xs font-semibold text-neutral-800">Publish immediately on website</span>
                        </label>
                      </div>
                    </div>

                    {/* SEO Metadata Card */}
                    <div className="bg-[#FF5A1F]/5 p-5 rounded-2xl border border-[#FF5A1F]/20 space-y-3">
                      <div className="text-xs font-bold uppercase tracking-wider text-[#FF5A1F]">🔍 Search Engine Optimization (SEO)</div>
                      <div>
                        <label className="block text-xs font-semibold text-neutral-700 mb-1">SEO Meta Title</label>
                        <input
                          value={blogForm.seo_title || ""}
                          onChange={(e) => setBlogForm({ ...blogForm, seo_title: e.target.value })}
                          className="w-full bg-white border border-black/10 rounded-xl px-3.5 py-2 text-xs outline-none focus:border-[#FF5A1F]"
                          placeholder="Meta title (defaults to post title)"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-neutral-700 mb-1">SEO Meta Keywords</label>
                        <input
                          value={blogForm.seo_keywords || ""}
                          onChange={(e) => setBlogForm({ ...blogForm, seo_keywords: e.target.value })}
                          className="w-full bg-white border border-black/10 rounded-xl px-3.5 py-2 text-xs outline-none focus:border-[#FF5A1F]"
                          placeholder="e.g. AI, Machine Learning, HRMS"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-neutral-700 mb-1">SEO Meta Description</label>
                        <textarea
                          rows={2}
                          value={blogForm.seo_description || ""}
                          onChange={(e) => setBlogForm({ ...blogForm, seo_description: e.target.value })}
                          className="w-full bg-white border border-black/10 rounded-xl px-3.5 py-2 text-xs outline-none focus:border-[#FF5A1F] resize-none"
                          placeholder="Search snippet description..."
                        />
                      </div>
                    </div>
                  </div>
                </form>
              </div>
            )}
          </div>
        )}


        {/* JOB OPENINGS TAB (Full CRUD) */}
        {tab === "jobs" && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold font-display">Active Job Openings & Internships</h2>
              <button
                onClick={() => setJobForm({ title: "", type: "Full-time", location: "Remote", team: "Engineering", category: "fulltime" })}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#FF5A1F] text-white font-semibold text-sm hover:bg-[#ff6d38] transition-colors shadow-md"
              >
                <Plus className="w-4 h-4" /> Add Open Position
              </button>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              {jobs.map((j) => (
                <div key={j.id} className="bg-white border border-black/5 rounded-2xl p-5 shadow-sm flex items-center justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-heading font-bold text-lg text-[#0B0B0B]">{j.title}</h3>
                      <span className={`text-[10px] font-bold uppercase tracking-wider px-2.5 py-0.5 rounded-full ${j.category === "internship" ? "bg-amber-100 text-amber-800" : "bg-blue-100 text-blue-800"}`}>
                        {j.category === "internship" ? "Internship" : "Full-Time"}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-neutral-500">
                      <span>💼 {j.type}</span>
                      <span>📍 {j.location}</span>
                      <span>🏷️ {j.team}</span>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setJobForm(j)}
                      className="w-9 h-9 rounded-lg bg-neutral-100 grid place-items-center hover:bg-[#FF5A1F] hover:text-white transition-colors"
                      title="Edit Job"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => deleteJob(j.id)}
                      className="w-9 h-9 rounded-lg bg-neutral-100 grid place-items-center hover:bg-red-500 hover:text-white transition-colors"
                      title="Delete Job"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* COMPANY SETTINGS & ADDRESSES TAB */}
        {tab === "settings" && (
          <div className="bg-white border border-black/5 rounded-3xl p-6 sm:p-8 shadow-sm max-w-3xl">
            <h2 className="text-xl font-bold font-display mb-2">Company Information & Office Addresses</h2>
            <p className="text-neutral-500 text-sm mb-6">Updates made here instantly update the Footer, Contact Page, and Legal notices live on the website.</p>

            <form onSubmit={saveSettings} className="space-y-5">
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-neutral-600 mb-1">Corporate Office 1 (HQ Gwalior) *</label>
                <textarea
                  required
                  rows={2}
                  className="w-full bg-[#FAF9F6] border border-black/10 rounded-xl p-3.5 text-sm outline-none focus:border-[#FF5A1F] resize-none"
                  value={settings.corporate_address}
                  onChange={(e) => setSettings({ ...settings, corporate_address: e.target.value })}
                />
              </div>

              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-neutral-600 mb-1">Corporate Office 2 (Gurugram Branch) *</label>
                <textarea
                  required
                  rows={2}
                  className="w-full bg-[#FAF9F6] border border-black/10 rounded-xl p-3.5 text-sm outline-none focus:border-[#FF5A1F] resize-none"
                  value={settings.corporate_address_2 || "Sector 23A, Gurugram, Haryana, India"}
                  onChange={(e) => setSettings({ ...settings, corporate_address_2: e.target.value })}
                />
              </div>

              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-neutral-600 mb-1">Registered Office Address *</label>
                <textarea
                  required
                  rows={2}
                  className="w-full bg-[#FAF9F6] border border-black/10 rounded-xl p-3.5 text-sm outline-none focus:border-[#FF5A1F] resize-none"
                  value={settings.registered_address}
                  onChange={(e) => setSettings({ ...settings, registered_address: e.target.value })}
                />
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-neutral-600 mb-1">Primary Email</label>
                  <input
                    required
                    type="email"
                    className="w-full bg-[#FAF9F6] border border-black/10 rounded-xl px-4 py-3 text-sm outline-none focus:border-[#FF5A1F]"
                    value={settings.email || "info@neuravolt.in"}
                    onChange={(e) => setSettings({ ...settings, email: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-neutral-600 mb-1">Phone / WhatsApp Number</label>
                  <input
                    required
                    type="text"
                    className="w-full bg-[#FAF9F6] border border-black/10 rounded-xl px-4 py-3 text-sm outline-none focus:border-[#FF5A1F]"
                    value={settings.phone || "+91 9202243725"}
                    onChange={(e) => setSettings({ ...settings, phone: e.target.value, whatsapp: e.target.value })}
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-neutral-600 mb-1">Legal Entity Name</label>
                <input
                  required
                  type="text"
                  className="w-full bg-[#FAF9F6] border border-black/10 rounded-xl px-4 py-3 text-sm outline-none focus:border-[#FF5A1F]"
                  value={settings.legal_entity}
                  onChange={(e) => setSettings({ ...settings, legal_entity: e.target.value })}
                />
              </div>

              <div className="pt-2">
                <button
                  type="submit"
                  className="px-8 py-3.5 rounded-full bg-[#FF5A1F] text-white font-semibold text-sm hover:bg-[#ff6d38] transition-colors shadow-md"
                >
                  Save Settings & Update Website Live
                </button>
              </div>
            </form>
          </div>
        )}

        {/* PROJECTS TAB (Full CRUD) */}
        {tab === "projects" && (


          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-display text-xl font-bold">Manage Portfolio Projects</h2>
              <button
                onClick={() =>
                  setProjectForm({
                    title: "",
                    domain: "",
                    cat: "Web",
                    desc: "",
                    tech: "React, FastAPI, MongoDB",
                    img: "https://images.unsplash.com/photo-1503387762-592deb58ef4e?w=1000&q=80",
                  })
                }
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#FF5A1F] text-white text-sm font-semibold hover:bg-[#ff6d38] transition-all shadow-md"
              >
                <Plus className="w-4 h-4" /> Add Project
              </button>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
              {projects.map((p) => (
                <div key={p.id || p.slug} className="bg-white border border-black/10 rounded-2xl p-5 shadow-sm flex flex-col justify-between">
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-[#FF5A1F]/10 text-[#FF5A1F]">{p.cat}</span>
                      {p.domain && <span className="text-xs font-mono text-neutral-500">{p.domain}</span>}
                    </div>
                    <h3 className="font-heading font-bold text-lg text-neutral-900">{p.title}</h3>
                    <p className="text-neutral-500 text-xs sm:text-sm mt-1 mb-3 line-clamp-2">{p.desc}</p>
                    <div className="flex flex-wrap gap-1 mb-4">
                      {(Array.isArray(p.tech) ? p.tech : []).slice(0, 3).map((t) => (
                        <span key={t} className="text-[11px] px-2 py-0.5 rounded-full bg-neutral-100 text-neutral-600 font-medium">{t}</span>
                      ))}
                    </div>
                  </div>
                  <div className="flex items-center justify-end gap-2 pt-3 border-t border-black/5">
                    <button
                      onClick={() => setProjectForm({ ...p, tech: Array.isArray(p.tech) ? p.tech.join(", ") : p.tech })}
                      className="px-3 py-1.5 rounded-lg bg-neutral-100 text-neutral-700 text-xs font-semibold inline-flex items-center gap-1 hover:bg-[#FF5A1F] hover:text-white transition-all"
                    >
                      <Edit2 className="w-3.5 h-3.5" /> Edit
                    </button>
                    <button
                      onClick={() => deleteProject(p.id)}
                      className="px-3 py-1.5 rounded-lg bg-red-50 text-red-600 text-xs font-semibold inline-flex items-center gap-1 hover:bg-red-500 hover:text-white transition-all"
                    >
                      <Trash2 className="w-3.5 h-3.5" /> Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* CLIENTS TAB (Full CRUD) */}
        {tab === "clients" && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-display text-xl font-bold">Manage Client Brands ("Trusted By")</h2>
              <button
                onClick={() => setClientForm({ name: "", domain: "" })}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#FF5A1F] text-white text-sm font-semibold hover:bg-[#ff6d38] transition-all shadow-md"
              >
                <Plus className="w-4 h-4" /> Add Client
              </button>
            </div>

            <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {clients.map((c) => {
                const logoUrl = c.logo ? c.logo : `https://www.google.com/s2/favicons?domain=${c.domain}&sz=128`;
                return (
                  <div key={c.id || c.domain} className="bg-white border border-black/10 rounded-2xl p-4 shadow-sm flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <img src={logoUrl} alt={c.name} className="w-8 h-8 rounded-lg object-contain shrink-0 border border-black/10" onError={(e) => { e.target.style.display = "none"; }} />
                      <div className="min-w-0">
                        <div className="font-heading font-bold text-sm truncate">{c.name}</div>
                        <div className="text-xs font-mono text-neutral-400 truncate">{c.domain}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <button onClick={() => setClientForm(c)} className="w-7 h-7 rounded-lg bg-neutral-100 grid place-items-center hover:bg-[#FF5A1F] hover:text-white transition-colors" title="Edit">
                        <Edit2 className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => deleteClient(c.id)} className="w-7 h-7 rounded-lg bg-red-50 text-red-600 grid place-items-center hover:bg-red-500 hover:text-white transition-colors" title="Delete">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* SUBSCRIBERS TAB */}
        {tab === "subscribers" && (
          <div className="bg-white border border-black/10 rounded-2xl divide-y divide-black/5 shadow-sm" data-testid="subs-list">
            {subs.length === 0 && <p className="text-neutral-500 p-5">No newsletter subscribers yet.</p>}
            {subs.map((s) => (
              <div key={s.id} className="flex items-center justify-between px-5 py-4">
                <span className="text-neutral-800 font-medium text-sm">{s.email}</span>
                <span className="text-xs text-neutral-400">{new Date(s.created_at).toLocaleDateString()}</span>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* PROJECT EDIT / ADD MODAL */}
      {projectForm && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl p-6 sm:p-8 max-w-lg w-full shadow-2xl relative max-h-[90vh] overflow-y-auto">
            <button onClick={() => setProjectForm(null)} className="absolute top-5 right-5 w-8 h-8 rounded-full bg-neutral-100 grid place-items-center text-neutral-500 hover:text-black">
              <X className="w-4 h-4" />
            </button>
            <h3 className="font-display text-2xl font-bold mb-5">{projectForm.id ? "Edit Project" : "Add New Project"}</h3>
            <form onSubmit={saveProject} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold uppercase text-neutral-500 mb-1">Project Title</label>
                <input required type="text" value={projectForm.title} onChange={(e) => setProjectForm({ ...projectForm, title: e.target.value })} className="w-full px-4 py-2.5 rounded-xl border border-black/10 outline-none focus:border-[#FF5A1F]" placeholder="e.g. BuildHealer" />
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase text-neutral-500 mb-1">Domain Name</label>
                <input required type="text" value={projectForm.domain} onChange={(e) => setProjectForm({ ...projectForm, domain: e.target.value })} className="w-full px-4 py-2.5 rounded-xl border border-black/10 outline-none focus:border-[#FF5A1F]" placeholder="e.g. buildhealer.com" />
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase text-neutral-500 mb-1">Category</label>
                <select value={projectForm.cat} onChange={(e) => setProjectForm({ ...projectForm, cat: e.target.value })} className="w-full px-4 py-2.5 rounded-xl border border-black/10 outline-none focus:border-[#FF5A1F]">
                  <option value="Web">Web</option>
                  <option value="Mobile">Mobile</option>
                  <option value="AI">AI</option>
                  <option value="Cloud">Cloud</option>
                  <option value="ERP">ERP</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase text-neutral-500 mb-1">Tech Stack (comma separated)</label>
                <input type="text" value={projectForm.tech} onChange={(e) => setProjectForm({ ...projectForm, tech: e.target.value })} className="w-full px-4 py-2.5 rounded-xl border border-black/10 outline-none focus:border-[#FF5A1F]" placeholder="React, FastAPI, MongoDB" />
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase text-neutral-500 mb-1">Description</label>
                <textarea required rows={3} value={projectForm.desc} onChange={(e) => setProjectForm({ ...projectForm, desc: e.target.value })} className="w-full px-4 py-2.5 rounded-xl border border-black/10 outline-none focus:border-[#FF5A1F]" placeholder="Project summary..." />
              </div>
              {/* Cover Image — Upload / URL / Screenshot */}
              <div>
                <label className="block text-xs font-semibold uppercase text-neutral-500 mb-2">Cover Image</label>

                {/* Drag & Drop Upload Zone */}
                <label
                  htmlFor="proj-img-upload"
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={async (e) => {
                    e.preventDefault();
                    const file = e.dataTransfer.files[0];
                    if (!file) return;
                    setImgUploading(true);
                    try {
                      const fd = new FormData();
                      fd.append("file", file);
                      const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
                      const res = await fetch(`${backendUrl}/api/upload/image`, { method: "POST", body: fd });
                      const data = await res.json();
                      if (data.url) { setProjectForm({ ...projectForm, img: data.url }); toast.success("Image uploaded!"); }
                      else toast.error("Upload failed");
                    } catch { toast.error("Upload error"); }
                    finally { setImgUploading(false); }
                  }}
                  className="flex flex-col items-center justify-center gap-2 w-full h-28 rounded-xl border-2 border-dashed border-[#FF5A1F]/40 bg-[#FF5A1F]/5 hover:bg-[#FF5A1F]/10 cursor-pointer transition-all mb-2"
                >
                  {imgUploading ? (
                    <div className="flex items-center gap-2 text-[#FF5A1F] text-sm font-semibold">
                      <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/></svg>
                      Uploading...
                    </div>
                  ) : (
                    <>
                      <svg className="w-8 h-8 text-[#FF5A1F]/60" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M12 12V4m0 0L8.5 7.5M12 4l3.5 3.5"/></svg>
                      <span className="text-sm text-neutral-500">Click or drag & drop image here</span>
                      <span className="text-[11px] text-neutral-400">JPG, PNG, WEBP, GIF (max 10 MB)</span>
                    </>
                  )}
                  <input
                    id="proj-img-upload"
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      setImgUploading(true);
                      try {
                        const fd = new FormData();
                        fd.append("file", file);
                        const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
                      const res = await fetch(`${backendUrl}/api/upload/image`, { method: "POST", body: fd });
                        const data = await res.json();
                        if (data.url) { setProjectForm({ ...projectForm, img: data.url }); toast.success("Image uploaded!"); }
                        else toast.error("Upload failed");
                      } catch { toast.error("Upload error"); }
                      finally { setImgUploading(false); }
                    }}
                  />
                </label>

                {/* Website Screenshot buttons */}
                {projectForm.domain && (
                  <div className="flex gap-2 mb-2">
                    <button
                      type="button"
                      onClick={() => setProjectForm({ ...projectForm, img: `https://image.thum.io/get/width/1200/crop/630/https://${projectForm.domain}` })}
                      className="flex-1 text-xs py-2 px-3 rounded-lg bg-purple-50 text-purple-600 font-semibold border border-purple-100 hover:bg-purple-100 transition-all"
                    >
                      🌐 Website Screenshot
                    </button>
                  </div>
                )}

                {/* Manual URL fallback */}
                <input
                  type="text"
                  value={projectForm.img}
                  onChange={(e) => setProjectForm({ ...projectForm, img: e.target.value })}
                  className="w-full px-4 py-2.5 rounded-xl border border-black/10 outline-none focus:border-[#FF5A1F] text-sm"
                  placeholder="Or paste image URL directly..."
                />

                {/* Live Preview */}
                {projectForm.img && (
                  <div className="mt-3 rounded-xl overflow-hidden border border-black/10 aspect-[16/9] bg-neutral-50 relative group">
                    <img
                      src={fixImgUrl(projectForm.img)}
                      alt="Preview"
                      className="w-full h-full object-cover"
                      onError={(e) => { e.target.src = "https://placehold.co/1200x630/f5f5f5/999?text=Image+Not+Found"; }}
                    />
                    <button
                      type="button"
                      onClick={() => setProjectForm({ ...projectForm, img: "" })}
                      className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/60 text-white grid place-items-center opacity-0 group-hover:opacity-100 transition-all hover:bg-red-500"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}
              </div>

              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setProjectForm(null)} className="flex-1 py-3 rounded-xl border border-black/10 font-semibold text-sm hover:bg-neutral-50">Cancel</button>
                <button type="submit" className="flex-1 py-3 rounded-xl bg-[#FF5A1F] text-white font-semibold text-sm hover:bg-[#ff6d38] shadow-md">Save Project</button>
              </div>
            </form>
          </div>
        </div>
      )}


      {/* CLIENT EDIT / ADD MODAL */}
      {clientForm && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl p-6 sm:p-8 max-w-md w-full shadow-2xl relative">
            <button onClick={() => setClientForm(null)} className="absolute top-5 right-5 w-8 h-8 rounded-full bg-neutral-100 grid place-items-center text-neutral-500 hover:text-black">
              <X className="w-4 h-4" />
            </button>
            <h3 className="font-display text-2xl font-bold mb-5">{clientForm.id ? "Edit Client Brand" : "Add New Client Brand"}</h3>
            <form onSubmit={saveClient} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold uppercase text-neutral-500 mb-1">Brand Name</label>
                <input required type="text" value={clientForm.name} onChange={(e) => setClientForm({ ...clientForm, name: e.target.value })} className="w-full px-4 py-2.5 rounded-xl border border-black/10 outline-none focus:border-[#FF5A1F]" placeholder="e.g. PawsUp UAE" />
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase text-neutral-500 mb-1">Domain Name</label>
                <input required type="text" value={clientForm.domain} onChange={(e) => setClientForm({ ...clientForm, domain: e.target.value })} className="w-full px-4 py-2.5 rounded-xl border border-black/10 outline-none focus:border-[#FF5A1F]" placeholder="e.g. pawsup.ae" />
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase text-neutral-500 mb-1">Custom Logo URL (optional)</label>
                <input type="url" value={clientForm.logo || ""} onChange={(e) => setClientForm({ ...clientForm, logo: e.target.value })} className="w-full px-4 py-2.5 rounded-xl border border-black/10 outline-none focus:border-[#FF5A1F]" placeholder="https://..." />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setClientForm(null)} className="flex-1 py-3 rounded-xl border border-black/10 font-semibold text-sm hover:bg-neutral-50">Cancel</button>
                <button type="submit" className="flex-1 py-3 rounded-xl bg-[#FF5A1F] text-white font-semibold text-sm hover:bg-[#ff6d38] shadow-md">Save Client</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* JOB OPENING EDIT / ADD MODAL */}

      {jobForm && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl p-6 sm:p-8 max-w-lg w-full shadow-2xl relative">
            <button onClick={() => setJobForm(null)} className="absolute top-5 right-5 w-8 h-8 rounded-full bg-neutral-100 grid place-items-center text-neutral-500 hover:text-black">
              <X className="w-4 h-4" />
            </button>
            <h3 className="font-display text-2xl font-bold mb-5">{jobForm.id ? "Edit Open Position" : "Add New Open Position"}</h3>
            <form onSubmit={saveJob} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold uppercase text-neutral-500 mb-1">Job Title *</label>
                <input required type="text" value={jobForm.title} onChange={(e) => setJobForm({ ...jobForm, title: e.target.value })} className="w-full px-4 py-2.5 rounded-xl border border-black/10 outline-none focus:border-[#FF5A1F]" placeholder="e.g. Senior Frontend Developer" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold uppercase text-neutral-500 mb-1">Category *</label>
                  <select
                    className="w-full px-4 py-2.5 rounded-xl border border-black/10 outline-none focus:border-[#FF5A1F] bg-white text-sm"
                    value={jobForm.category || "fulltime"}
                    onChange={(e) => setJobForm({ ...jobForm, category: e.target.value })}
                  >
                    <option value="fulltime">Full-Time Role</option>
                    <option value="internship">Internship</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase text-neutral-500 mb-1">Type *</label>
                  <input required type="text" value={jobForm.type} onChange={(e) => setJobForm({ ...jobForm, type: e.target.value })} className="w-full px-4 py-2.5 rounded-xl border border-black/10 outline-none focus:border-[#FF5A1F]" placeholder="Full-time / Internship" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold uppercase text-neutral-500 mb-1">Location *</label>
                  <input required type="text" value={jobForm.location} onChange={(e) => setJobForm({ ...jobForm, location: e.target.value })} className="w-full px-4 py-2.5 rounded-xl border border-black/10 outline-none focus:border-[#FF5A1F]" placeholder="Remote / Hybrid" />
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase text-neutral-500 mb-1">Team *</label>
                  <input required type="text" value={jobForm.team} onChange={(e) => setJobForm({ ...jobForm, team: e.target.value })} className="w-full px-4 py-2.5 rounded-xl border border-black/10 outline-none focus:border-[#FF5A1F]" placeholder="Engineering / Design / AI" />
                </div>
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setJobForm(null)} className="flex-1 py-3 rounded-xl border border-black/10 font-semibold text-sm hover:bg-neutral-50">Cancel</button>
                <button type="submit" className="flex-1 py-3 rounded-xl bg-[#FF5A1F] text-white font-semibold text-sm hover:bg-[#ff6d38] shadow-md">Save Position</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* TEAM EDIT / ADD MODAL */}
      {teamForm && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl p-6 sm:p-8 max-w-md w-full shadow-2xl relative max-h-[90vh] overflow-y-auto">
            <button onClick={() => setTeamForm(null)} className="absolute top-5 right-5 w-8 h-8 rounded-full bg-neutral-100 grid place-items-center text-neutral-500 hover:text-black">
              <X className="w-4 h-4" />
            </button>
            <h3 className="font-display text-2xl font-bold mb-5">{teamForm.id ? "Edit Team Member" : "Add Team Member"}</h3>
            <form onSubmit={saveTeamMember} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold uppercase text-neutral-500 mb-1">Full Name *</label>
                <input
                  required
                  type="text"
                  value={teamForm.name}
                  onChange={(e) => setTeamForm({ ...teamForm, name: e.target.value })}
                  className="w-full px-4 py-2.5 rounded-xl border border-black/10 outline-none focus:border-[#FF5A1F]"
                  placeholder="e.g. Ajay Pratap Tomar"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold uppercase text-neutral-500 mb-1">Role / Designation *</label>
                <input
                  required
                  type="text"
                  value={teamForm.role}
                  onChange={(e) => setTeamForm({ ...teamForm, role: e.target.value })}
                  className="w-full px-4 py-2.5 rounded-xl border border-black/10 outline-none focus:border-[#FF5A1F]"
                  placeholder="e.g. Founder & CEO"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold uppercase text-neutral-500 mb-1">Initials (for avatar fallback)</label>
                <input
                  type="text"
                  value={teamForm.initials}
                  onChange={(e) => setTeamForm({ ...teamForm, initials: e.target.value })}
                  className="w-full px-4 py-2.5 rounded-xl border border-black/10 outline-none focus:border-[#FF5A1F]"
                  placeholder="e.g. AT"
                />
              </div>

              {/* Profile Photo Uploader */}
              <div>
                <label className="block text-xs font-semibold uppercase text-neutral-500 mb-2">Profile Photo</label>
                <label
                  htmlFor="team-img-upload"
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={async (e) => {
                    e.preventDefault();
                    const file = e.dataTransfer.files[0];
                    if (!file) return;
                    setImgUploading(true);
                    try {
                      const fd = new FormData();
                      fd.append("file", file);
                      const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
                      const res = await fetch(`${backendUrl}/api/upload/image`, { method: "POST", body: fd });
                      const data = await res.json();
                      if (data.url) { setTeamForm({ ...teamForm, img: data.url }); toast.success("Photo uploaded!"); }
                      else toast.error("Upload failed");
                    } catch { toast.error("Upload error"); }
                    finally { setImgUploading(false); }
                  }}
                  className="flex flex-col items-center justify-center gap-2 w-full h-24 rounded-xl border-2 border-dashed border-[#FF5A1F]/40 bg-[#FF5A1F]/5 hover:bg-[#FF5A1F]/10 cursor-pointer transition-all mb-2"
                >
                  {imgUploading ? (
                    <div className="flex items-center gap-2 text-[#FF5A1F] text-sm font-semibold">
                      <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/></svg>
                      Uploading...
                    </div>
                  ) : (
                    <>
                      <span className="text-xs font-semibold text-[#FF5A1F]">📸 Drag & Drop Photo or Click to Upload</span>
                      <span className="text-[11px] text-neutral-400">JPG, PNG, WEBP (square photo recommended)</span>
                    </>
                  )}
                  <input
                    id="team-img-upload"
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      setImgUploading(true);
                      try {
                        const fd = new FormData();
                        fd.append("file", file);
                        const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
                      const res = await fetch(`${backendUrl}/api/upload/image`, { method: "POST", body: fd });
                        const data = await res.json();
                        if (data.url) { setTeamForm({ ...teamForm, img: data.url }); toast.success("Photo uploaded!"); }
                        else toast.error("Upload failed");
                      } catch { toast.error("Upload error"); }
                      finally { setImgUploading(false); }
                    }}
                  />
                </label>

                <input
                  type="text"
                  value={teamForm.img || ""}
                  onChange={(e) => setTeamForm({ ...teamForm, img: e.target.value })}
                  className="w-full px-4 py-2.5 rounded-xl border border-black/10 outline-none focus:border-[#FF5A1F] text-xs"
                  placeholder="Or paste photo URL..."
                />

                {teamForm.img && (
                  <div className="mt-2 flex items-center gap-3 p-2 bg-neutral-50 border border-black/10 rounded-xl relative">
                    <img src={fixImgUrl(teamForm.img)} alt="Preview" className="w-12 h-12 rounded-lg object-cover" />
                    <div className="flex-1 text-xs text-neutral-500 truncate">{fixImgUrl(teamForm.img)}</div>
                    <button type="button" onClick={() => setTeamForm({ ...teamForm, img: "" })} className="p-1 rounded-full text-neutral-400 hover:text-red-500">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-xs font-semibold uppercase text-neutral-500 mb-1">Short Bio / Tagline</label>
                <textarea
                  rows={2}
                  value={teamForm.bio || ""}
                  onChange={(e) => setTeamForm({ ...teamForm, bio: e.target.value })}
                  className="w-full px-4 py-2.5 rounded-xl border border-black/10 outline-none focus:border-[#FF5A1F] text-xs"
                  placeholder="Brief 1-line bio..."
                />
              </div>

              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setTeamForm(null)} className="flex-1 py-3 rounded-xl border border-black/10 font-semibold text-sm hover:bg-neutral-50">
                  Cancel
                </button>
                <button type="submit" className="flex-1 py-3 rounded-xl bg-[#FF5A1F] text-white font-semibold text-sm hover:bg-[#ff6d38] shadow-md">
                  Save Member
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}


